import { useEffect, useMemo, useState } from "react"
import cn from "classnames"
import { stringify } from "querystringify"
import { Link, useNavigate } from "react-router-dom"
import { Div } from "trunx"

import { Box, Button, ButtonLink, Img, Text, TextWithIcon } from "ui-kit/components-branded/index"
import { ContactsModal, Icon } from "components"
import { NewBookingPageLink } from "app/AppRouter/links"
import { Booking, BookingPsy, CancellationInfo, createPath } from "modules/ClientAccount/utils"
import { Analytics } from "utils/analytics"
import { ProfileAndInfo } from "modules/ClientAccount/components/Sessions/ProfileAndInfo"
import { canStartCall } from "modules/Call/common/utils"
import { renderCityWithDetails } from "utils/helpers/psychologistUtil"
import { APP_STATUSES } from "utils/AppStatusContext"
import { formatPrice } from "utils/utils"
import { formatToShort } from "utils/datetime-format-utils"
import { useShowPrice } from "utils/hooks/useShowPrice"

export type Datetime = string

export interface Session {
    price: number
    forFree: boolean
    cancellationDatetime?: Datetime
    cancellationInitiator?: CancellationInfo["initiator"]
    sessionEnded: boolean
    isRefundPossible: boolean
    isRefundApplied: boolean
    paid: boolean
    canceled: boolean
    isOffline: boolean
    bookNextSession: boolean
    showPaymentBlock: boolean
    createdByPsychologist: boolean
    startDatetime: Datetime
    endDatetime: Datetime
    previousStartDatetime: Datetime | null
    startDatetimeChangedDatetime: Datetime | null
    psy: BookingPsy
    bookingHash: string
    currency: Booking["currency"]
    hasFixedSessionTime: boolean
    call?: Booking["call"]
    isBookingAvailableForCall: Booking["is_booking_available_for_call"]
}

function CreatedByPsyBlock({ className, isAccent }: { className: string; isAccent: boolean }) {
    return (
        <Text isAccent={isAccent} className={className}>
            <Img className="sessions__emoji-medium" src="writing-hand-white-emoji.png" />
            Психолог записал вас на&nbsp;сессию.
        </Text>
    )
}

function SessionNotPaid({ className, isAccent }: { className: string; isAccent: boolean }) {
    return (
        <Text isAccent={isAccent} className={className}>
            <Img className="sessions__emoji-small" src="credit-card-emoji.png" />
            Эта сессия еще не&nbsp;оплачена.
        </Text>
    )
}

const cancellationInitiatorText: Record<CancellationInfo["initiator"], string> = {
    system: "Отмена из-за неоплаты",
    admin: "Альтер отменил",
    client: "Вы отменили",
    psychologist: "Психолог отменил",
}

function AttentionBlock({
    showCreatedByPsychologist,
    price,
    cancelDate,
    bookingHash,
    hashId,
    cancelInitiator,
    canceled,
    currency,
}: {
    showCreatedByPsychologist: boolean
    price: number
    cancelDate?: Datetime
    bookingHash: string
    hashId: string
    cancelInitiator?: CancellationInfo["initiator"]
    canceled: boolean
    currency: Booking["currency"]
}) {
    const showPrice = useShowPrice()

    return (
        <div
            className={cn("sessions__go-to-payment", {
                "sessions__go-to-payment--smaller-padding": !canceled,
                "sessions__go-to-payment--bigger-padding": canceled,
            })}
        >
            {canceled ? (
                <div className="sessions__go-to-payment__text">
                    <Img className="sessions__emoji-small" src="no-entry-sign-emoji.png" />
                    {cancelInitiator &&
                        (cancellationInitiatorText[cancelInitiator] || "Отменена")}{" "}
                    {cancelDate && formatToShort(cancelDate)}
                </div>
            ) : (
                <>
                    {showCreatedByPsychologist ? (
                        <>
                            <CreatedByPsyBlock className="is-hidden-desktop" isAccent={false} />
                            <CreatedByPsyBlock className="is-hidden-touch" isAccent={true} />
                        </>
                    ) : (
                        <>
                            <SessionNotPaid className="is-hidden-desktop" isAccent={false} />
                            <SessionNotPaid className="is-hidden-touch" isAccent={true} />
                        </>
                    )}
                    <Button
                        as={ButtonLink}
                        className="sessions__go-to-payment__button is-borderless"
                        href={`/payment/${bookingHash}${stringify({ hash_id: hashId }, "?")}`}
                        target="_blank"
                        onClick={() => {
                            Analytics.lk_pay_started()
                        }}
                    >
                        Оплатить {showPrice && formatPrice(price, currency)}
                    </Button>
                </>
            )}
        </div>
    )
}

function SessionLocation({
    session,
    setModalIsShown,
}: {
    session: Session
    setModalIsShown: (value: boolean) => void
}) {
    const navigate = useNavigate()

    if (session.isBookingAvailableForCall) {
        return (
            <div className="sessions__location__alter-video-call">
                <Button
                    onClick={() => {
                        Analytics.before_primary_action_connect()
                        navigate(`/my/call/lobby/${session.bookingHash}`)
                    }}
                    disabled={!canStartCall(session.call?.status)}
                    color="primary"
                >
                    <Text isAccent>Начать звонок</Text>
                </Button>
                {!canStartCall(session.call?.status) && (
                    <Text className="sessions__location__alter-video-call__text">
                        Вы&nbsp;сможете присоединиться за&nbsp;10&nbsp;минут до&nbsp;начала.
                    </Text>
                )}
            </div>
        )
    }

    if (session.isOffline) {
        return (
            <div className="sessions__location__offline">
                <div className="sessions__location__offline__text">
                    <Icon type="nav" mr={2} color="blue-3" size={24} />
                    <Text>
                        {renderCityWithDetails(session.psy.city, session.psy.location_details)}
                    </Text>
                </div>
                <Button onClick={() => setModalIsShown(true)} color="primary">
                    <Text isAccent>Как добраться</Text>
                </Button>
            </div>
        )
    }

    return (
        <div className="sessions__location__online">
            <Button onClick={() => setModalIsShown(true)} color="primary">
                <Text isAccent>Как подключиться к&nbsp;сессии</Text>
            </Button>
        </div>
    )
}

function MoveSession({ session }: { session: Session }) {
    const showPrice = useShowPrice()

    const buttonText = useMemo(() => {
        return session.isRefundPossible
            ? "Перенести или отменить"
            : "Перенос или отмена без возврата"
    }, [session.isRefundPossible])

    const onClick = () => Analytics.lk_reschedule_cancel_session_started()

    return (
        <>
            <a
                href={`/booking/${session.bookingHash}`}
                target="_blank"
                className="mgt-3 mgb-3 is-hidden-desktop button blm-button is-fullwidth"
                onClick={onClick}
                rel="noreferrer"
            >
                {buttonText}
            </a>

            <hr className="sessions__card__hr mgt-3 mgb-4 is-hidden-touch" />

            <a
                href={`/booking/${session.bookingHash}`}
                target="_blank"
                className="sessions__card__secondary-button-link is-hidden-touch"
                onClick={onClick}
                rel="noreferrer"
            >
                {buttonText}
            </a>
            {showPrice && (
                <Text className="has-text-dark-60">
                    <>При переносе менее чем за&nbsp;сутки оплата не&nbsp;возвращается.</>
                </Text>
            )}
        </>
    )
}

function CardBottom({ session }: { session: Session }) {
    return (
        <>
            {session.sessionEnded || session.canceled ? (
                <>
                    <hr className="mgt-3 mgb-3 sessions__card__hr__not-mobile is-hidden-desktop" />
                    <NewBookingPageLink
                        hashId={session.psy.hash_id}
                        target="_blank"
                        className="button blm-button is-primary sessions__card__button-wrapper"
                        from={APP_STATUSES.client_account}
                    >
                        <Text isAccent>Записаться еще раз</Text>
                    </NewBookingPageLink>
                </>
            ) : (
                <>
                    <hr className="mgt-3 mgb-3 sessions__card__hr__not-mobile is-hidden-desktop" />
                    <hr className="mgt-4 mgb-3 sessions__card__hr__not-mobile is-hidden-touch" />
                    <NewBookingPageLink
                        hashId={session.psy.hash_id}
                        target="_blank"
                        className="button blm-button sessions__card__button-wrapper"
                        from={APP_STATUSES.client_account}
                    >
                        <Img className="sessions__emoji-small" src="pen-emoji.png" />
                        <Text isAccent>Записаться на&nbsp;следующую</Text>
                    </NewBookingPageLink>
                </>
            )}
        </>
    )
}

function Card({ session }: { session: Session }) {
    const [isModalShown, setModalIsShown] = useState(false)
    useEffect(() => {
        if (isModalShown) {
            if (session.isOffline) {
                Analytics.lk_session_map_offline()
            } else {
                Analytics.lk_session_map_online()
            }
        }
    }, [isModalShown])
    return (
        <>
            <Box>
                {(session.showPaymentBlock || session.canceled) && (
                    <AttentionBlock
                        showCreatedByPsychologist={
                            session.createdByPsychologist && !session.sessionEnded
                        }
                        price={session.price}
                        cancelDate={session.cancellationDatetime}
                        cancelInitiator={session.cancellationInitiator}
                        bookingHash={session.bookingHash}
                        hashId={session.psy.hash_id}
                        canceled={session.canceled}
                        currency={session.currency}
                    />
                )}
                <ProfileAndInfo session={session} />
                {!session.sessionEnded && !session.canceled && (
                    <>
                        <hr
                            className={cn("mgt-3 mgb-3 sessions__card__hr is-hidden-desktop", {
                                ["sessions__card__hr__not-mobile"]: !session.isOffline,
                            })}
                        />
                        <SessionLocation session={session} setModalIsShown={setModalIsShown} />
                        <MoveSession session={session} />
                    </>
                )}
                {session.bookNextSession && <CardBottom session={session} />}
            </Box>
            <ContactsModal
                isOffline={session.isOffline}
                isModalShown={isModalShown}
                phone={session.psy.phone_number}
                location={
                    session.isOffline
                        ? renderCityWithDetails(
                              session.psy.city,
                              session.psy.addresses,
                              session.psy.location_details,
                              <br />
                          )
                        : session.psy.videotelephony
                }
                hideModal={() => setModalIsShown(false)}
            />
        </>
    )
}

export function SessionsMenu({ title }: { title: string }) {
    return (
        <Div bulma={["level", "is-mobile"]} className="sessions">
            <Div bulma="level-left">
                <Text isAccent>{title}</Text>
            </Div>
            <Div bulma="level-right">
                <Link to={createPath("/sessions")}>
                    <TextWithIcon
                        isAccent
                        icon={
                            <Icon
                                type="arrow-right-date-picker"
                                size={24}
                                fill="currentColor"
                                className="sessions__arrow"
                            />
                        }
                        position="right"
                    >
                        Все сессии
                    </TextWithIcon>
                </Link>
            </Div>
        </Div>
    )
}

export function Sessions({ sessions }: { sessions: Session[] }) {
    return (
        <>
            {sessions.map((session) => (
                <Card key={session.bookingHash} session={session} />
            ))}
        </>
    )
}
